import type { FolderSummary } from '../types/folder.types';

import type { FolderSearchPage } from './fetchers/folder';
import type { MyFoldersVariables } from './folder.queries';

/**
 * 내 폴더 검색 응답을 이 세션에서 아는 변경과 맞춘다.
 *
 * 내 폴더 검색(`/quizset-folders/my/search`)은 검색 인덱스를 읽어 원본보다 1초 안팎 늦다. 만들거나 지운
 * 직후의 응답에는 새 폴더가 빠지고 지운 폴더가 되살아난다. 결과를 알고 있으므로 응답 쪽에서 바로잡는다 —
 * 캐시를 고친 뒤 늦은 응답이 그걸 덮어써도 목록이 되돌아가지 않는다. 캐시 쪽은 `folderListCache`.
 */

/** 이 세션에서 지운 폴더. id 는 다시 쓰이지 않으므로 세션 동안 걸러도 안전하다. */
const deletedFolderIds = new Set<string>();

/**
 * 만든 폴더를 응답에 끼워 주는 시간. 인덱스 지연(1초 안팎)을 넉넉히 덮으면 된다.
 *
 * 영원히 기억하지 않는다 — 응답에 실리지 않는 한 계속 끼우게 되므로 시간이 지나면 잊는다.
 */
const CREATED_FOLDER_RECONCILE_TTL_MS = 15_000;

/**
 * 이 세션에서 만들었는데 아직 검색 응답에서 본 적 없는 폴더. 응답에 한 번 실리면 인덱스가 따라온 것이므로
 * 잊는다 — 계속 끼우면 뒤 페이지와 겹칠 수 있다.
 *
 * 만들 때 쓴 API 토큰(`credential`)을 함께 둔다. 모듈 메모리는 계정 경계를 모른다 — 로그아웃은 리로드 없이
 * 쿼리 캐시만 비우고, 다른 탭에서 계정을 바꾸면 이 탭의 요청 토큰이 바뀐다. 같은 토큰으로 받은 응답에만 끼워
 * 다른 계정의 내 폴더 목록에 섞이지 않게 한다.
 */
const unseenCreatedFolders = new Map<
  string,
  { folder: FolderSummary; credential: string; rememberedAt: number }
>();

export const rememberDeletedFolder = (id: string) => {
  deletedFolderIds.add(id);
  unseenCreatedFolders.delete(id);
};

export const rememberCreatedFolder = (
  folder: FolderSummary,
  credential: string,
) => {
  unseenCreatedFolders.set(folder.id, {
    folder,
    credential,
    rememberedAt: Date.now(),
  });
};

/** 아직 끼워 줄 만큼 최근에, 같은 토큰으로 만든 폴더만. 나중에 만든 것이 앞이다. */
const takeFreshCreatedFolders = (credential: string) => {
  const now = Date.now();
  for (const [id, entry] of unseenCreatedFolders) {
    if (now - entry.rememberedAt > CREATED_FOLDER_RECONCILE_TTL_MS)
      unseenCreatedFolders.delete(id);
  }
  return [...unseenCreatedFolders.values()]
    .filter(entry => entry.credential === credential)
    .map(entry => entry.folder)
    .reverse();
};

type ListShape = Pick<MyFoldersVariables, 'order' | 'categoryFilters'>;

/**
 * 만든 폴더를 맨 앞에 둘 수 있는 목록인가 — 최신순이고 검색어가 없을 때.
 *
 * 인기순(`POPULARITY` 와 일·주·월 변형)은 자리를 모른다. 정렬을 넘기지 않은 목록은 최신순으로 본다.
 */
export const canPrependCreatedFolder = (variables: ListShape | undefined) =>
  (variables?.order ?? 'RECENTLY') === 'RECENTLY' &&
  !variables?.categoryFilters?.criteria?.searchText;

/**
 * 검색 응답 한 페이지를 바로잡는다. 인덱스가 따라온 뒤의 응답에는 아무것도 바꾸지 않는다.
 *
 * - 지운 폴더가 실려 있으면 빼고 전체 개수도 그만큼 줄인다.
 * - 만든 폴더가 첫 페이지에 없으면 맨 앞에 끼우고 전체 개수를 그만큼 올린다. 이 응답을 받은 토큰
 *   (`credential`)으로 만든 폴더만 끼운다.
 */
export const reconcileFolderSearchPage = (
  page: FolderSearchPage,
  context: { variables?: ListShape; isFirstPage: boolean; credential: string },
): FolderSearchPage => {
  let items = page.items;
  let delta = 0;

  if (deletedFolderIds.size > 0) {
    items = items.filter(item => !deletedFolderIds.has(item.id));
    delta -= page.items.length - items.length;
  }

  for (const item of items) unseenCreatedFolders.delete(item.id);
  if (context.isFirstPage && canPrependCreatedFolder(context.variables)) {
    const created = takeFreshCreatedFolders(context.credential);
    items = [...created, ...items];
    delta += created.length;
  }

  if (delta === 0) return page;
  return {
    ...page,
    items,
    totalCount:
      page.totalCount === undefined
        ? undefined
        : Math.max(0, page.totalCount + delta),
  };
};
